import {
  AfterViewInit,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { IonAccordionGroup } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import { AuthService, UserData, UserRole } from './auth/auth.service';
import { CompanyService } from './company/services/company.service';
import { ProfileService } from './profile/services/profile.service';
import { LanguagesService } from './services/languages.service';
import { OfflineService } from './services/offline.service';
import { ToastService } from './services/toast.service';
import { FirebaseAnalyticsService } from './shared/services/firebase-analytics.service';
import { MoEngageService } from './shared/services/moengage.service';
import { VehicleInspectionService } from './vehicle-inspection/services/vehicle-inspection.service';
import { URLOpenListenerEvent } from '@capacitor/app';
import * as Sentry from '@sentry/angular';
import { App } from '@capacitor/app';
import { AdminInspectionService } from './admin/services/admin-inspection.service';
import { VehicleInspectionTrackingService } from './vehicle-inspection/services/vehicle-inspection-tracking.service';
import { TrainingsService } from './qualifications/services/trainings.service';
import { Browser } from '@capacitor/browser';
import { StorageService } from 'src/app/services/storage.service';
import { PreferencesService } from 'src/app/services/preferences.service';
import { ChatBubbleDragService } from './shared/services/chat-bubble-drag.service';
import { LoggerService } from './services/logger.service';
import { AppUpdateService } from './services/app-update.service';
import { GeolocationService } from './my-transfers/services/geolocation.service';
import { getBonusPercent } from './shared/model/status-bonus.model';
import { QualificationsService } from './qualifications/services/qualifictions.service';
import {
  DriverOnboardingStatus,
  DRIVER_ONBOARDING_STATUS_ORDER,
  onboardingStageProgress,
} from './profile/model/profile.model';
import {
  isProfileComplete,
  isCompanyComplete,
} from './shared/model/profile-completeness';

interface MenuItem {
  title: string;
  url?: string;
  icon: string;
  roles: UserRole[];
  action?: () => void;
}

/** Position of the terminal "onboarded" milestone in the global status order. */
const ONBOARDED_ORDER = DRIVER_ONBOARDING_STATUS_ORDER.indexOf(
  DriverOnboardingStatus.Onboarded,
);

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('accordionGroup', { static: true })
  accordionGroup: IonAccordionGroup;
  languageWasChanged = false;

  currentUserRole: UserRole | undefined;

  userPages: any[] = [];
  userProfilePages: any[] = [];
  userTrainingPages: any[] = [];
  userAdminInspectionPages: any[] = [];
  userAdminPages: any[] = [];

  syncInProgress = false;
  shouldForceErrorCount = 0;

  driverRoles = [
    UserRole.Driver,
    UserRole.SingleDriver,
    UserRole.ManagerDriver,
  ];

  managerRoles = [
    UserRole.Manager,
    UserRole.SingleDriver,
    UserRole.ManagerDriver,
  ];

  allRoles = [...this.driverRoles, ...this.managerRoles];

  pages: MenuItem[] = [];

  standardPages: MenuItem[] = [
    {
      title: 'movacarpro_menu_my_transfers',
      url: '/my-transfers/',
      icon: 'reader',
      roles: this.driverRoles,
    },
    {
      title: 'movacarpro_menu_tours',
      url: '/tours/',
      icon: 'file-tray-full',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_biddings',
      url: '/biddings/',
      icon: 'ticket',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_transfers',
      url: '/transfers/',
      icon: 'car',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_notifications',
      url: '/alerts/',
      icon: 'notifications',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_billings',
      url: '/billings/',
      icon: 'receipt',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_drivers',
      url: '/drivers/',
      icon: 'people',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_reports',
      url: '/reports/',
      icon: 'bar-chart',
      roles: this.managerRoles,
    },
    {
      title: 'movacarpro_menu_qualifications',
      url: '/qualifications/',
      icon: 'school',
      roles: this.allRoles,
    },
  ];

  onboardingEntry = {
    title: 'onboarding',
    url: '/onboarding',
    icon: 'person-circle',
    roles: this.allRoles,
  };

  private get isDriverUser(): boolean {
    const role = this.authService.role;

    return role !== UserRole.Manager && role !== UserRole.FleetManager;
  }

  get showPersonalDetails(): boolean {
    return this.isDriverUser;
  }

  get showOnboardingEntry(): boolean {
    return this.isDriverUser && this.profileService.onboardingEnabled;
  }

  profilePages = [
    {
      title: 'movacarpro_company_profile_title',
      url: '/company/',
      icon: 'business',
      roles: this.managerRoles,
    },
  ];

  adminInspectionPages = [
    {
      title: 'Create New Inspection',
      url: 'admin/inspection/create',
      icon: '',
      roles: this.allRoles,
    },
    {
      title: 'Edit Inspection',
      url: 'admin/inspection/edit',
      icon: '',
      roles: this.allRoles,
    },
  ];

  adminPages = [
    {
      title: 'Poker',
      url: '/poker',
      icon: 'game-controller-outline',
      roles: this.allRoles,
    },
    {
      title: 'Translation',
      url: 'admin/translation',
      icon: 'language',
      roles: this.allRoles,
    },
  ];

  UserRole = UserRole;

  private _adminMode = false;
  private _adminMenuActive = false;

  get adminMode(): boolean {
    return this._adminMode;
  }

  set adminMode(value: boolean) {
    this._adminMode = value;
    this.storageService.set('adminMode', value.toString());
  }

  get adminMenuActive(): boolean {
    return this._adminMenuActive;
  }

  set adminMenuActive(value: boolean) {
    this._adminMenuActive = value;
    this.storageService.set('adminMenuActive', value.toString());
  }

  isNativePlatform = Capacitor.isNativePlatform();

  private observer: MutationObserver;
  private logger = new LoggerService(AppComponent.name);

  constructor(
    public languagesService: LanguagesService,
    public authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private offlineService: OfflineService,
    public profileService: ProfileService,
    public companyService: CompanyService,
    private vehicleInspectionService: VehicleInspectionService,
    private firebaseAnalyticsService: FirebaseAnalyticsService,
    private moEngageService: MoEngageService,
    private trainingsService: TrainingsService,
    private zone: NgZone,
    private adminInspectionService: AdminInspectionService,
    private storageService: StorageService,
    private preferencesService: PreferencesService,
    private chatBubbleDragService: ChatBubbleDragService,
    private appUpdateService: AppUpdateService,
    private vehicleInspectionTrackingService: VehicleInspectionTrackingService,
    private geolocationService: GeolocationService,
    private qualificationsService: QualificationsService,
  ) {
    this.pages = [...this.standardPages];

    this.appUpdateService.initialize();
    this.initializeApp();
    this.loadAdminState();
    this.initBonusPanelState();
  }

  async ngOnInit(): Promise<void> {
    // iOS resilience: when the OS relaunches the app in the background after
    // the process was killed, tracking only resumes if BackgroundGeolocation
    // .ready() runs immediately and unconditionally. ready() can only ever resume tracking
    // for a user who already consented and was already being tracked.
    const hasConsentedToBackgroundLocation: boolean =
      await this.geolocationService.hasAcceptedBackgroundLocationDisclosure();

    if (hasConsentedToBackgroundLocation) {
      try {
        await this.vehicleInspectionTrackingService.initTracking();
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    await this.startFirebase();

    // Surface the prominent background-location disclosure during normal usagedebug/tracking
    // (Google Play requirement) rather than only deep inside the driver-only
    // pickup flow. Started here so it overlays straight away; the service
    // no-ops on web and once the disclosure has been accepted.
    const disclosurePromise: Promise<boolean> =
      this.geolocationService.ensureBackgroundLocationDisclosure();

    this.logger.info({
      message: 'Initializing MoEngage',
      identifier: 'app_component_moengage_initialize',
      method: 'ngOnInit',
    });

    await this.moEngageService.initialize(false);

    const disclosureAccepted: boolean = await disclosurePromise;

    await this.moEngageService.registerForPush();
    await this.moEngageService.setupAndroidPush();

    await this.offlineService.getNetworkStatus();

    if (!hasConsentedToBackgroundLocation && disclosureAccepted) {
      try {
        await this.vehicleInspectionTrackingService.initTracking();
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    // Keep the onboarding menu in sync with the live onboarding status: when the
    // driver completes their profile (status → profile_completed) the Company
    // tab must appear immediately, matching the onboarding stepper's unlock.
    // profile$ emits on every profile fetch/save — including the onboarding
    // page's re-fetch on entry. Guarded to a driver actively in the funnel
    // (ord 0..4), so managers / already-onboarded users are untouched.
    this.profileService.profile$.subscribe((profile) => {
      if (!profile || !this.userProfilePages) {
        return;
      }

      const order = DRIVER_ONBOARDING_STATUS_ORDER.indexOf(
        profile.onboardingStatus,
      );

      if (order < 0 || order >= ONBOARDED_ORDER) {
        return;
      }

      // Legacy drivers (onboarding disabled by the backend) now carry an
      // onboardingStatus too, but must keep their pre-onboarding menu — so skip
      // the onboarding-driven menu injection when the backend blocked it.
      if (!(profile.onboardingEnabled ?? true)) {
        return;
      }

      this.profileService.onboardingStatus = profile.onboardingStatus;
      this.addOnboardingMenuEntries();
    });

    this.authService.authSubject$.subscribe((authenticated: boolean) => {
      // `beforeAuthPage` is set inside each public page's ngOnInit, which for a
      // lazy-loaded page (e.g. reset-password) can run AFTER this subscription
      // fires at startup — a race that silently bounced logged-out users to
      // /login (mostly on web, where the awaits above resolve instantly).
      // The URL check is synchronous and race-free, so back the flag with it.
      const onBeforeAuthPage =
        this.authService.beforeAuthPage || this.authService.isBeforeAuthUrl();

      if (!authenticated && !onBeforeAuthPage) {
        this.router.navigate(['/login']);

        return;
      }

      if (this.adminMode && this.adminMenuActive) {
        if (!this.router.url.includes('/admin')) {
          this.router.navigate(['/admin']);
          return;
        }
      }

      if (onBeforeAuthPage) {
        return;
      }

      this.authService.getUserData().subscribe({
        next: async (userData) => {
          if (!userData) {
            return;
          }

          this.authService.role = userData.role;

          this.currentUserRole = userData.role;

          this.adminMode = this.isAdminUser(userData);

          this.authService.isAdmin.set(this.adminMode);

          this.addAdminModeTab();

          this.userPages = this.pages.filter((p) =>
            p.roles?.includes(userData.role),
          );

          this.userProfilePages = this.profilePages.filter((p) =>
            p.roles?.includes(userData.role),
          );

          this.userAdminInspectionPages = this.adminInspectionPages.filter(
            (p) => p.roles?.includes(userData.role),
          );

          this.userAdminPages = this.adminPages.filter((p) =>
            p.roles?.includes(userData.role),
          );

          if (this.languageWasChanged) {
            return;
          }

          this.setLanguageToUserLanguage(userData);

          if (userData.role === UserRole.FleetManager) {
            return;
          }

          const profile = await lastValueFrom(
            this.profileService.fetchContactProfile(),
          );

          if (!profile) {
            return;
          }

          this.profileService.premiumPartnerLevel = profile.premiumPartnerLevel;
          this.profileService.onboardingStatus = profile.onboardingStatus;

          // Only drivers onboard (Driver / ManagerDriver / SingleDriver). A pure
          // Manager lives in the Manager entity with no onboardingStatus and must
          // not be routed into the driver onboarding flow.
          if (
            (profile.onboardingEnabled ?? true) &&
            profile.onboardingStatus &&
            profile.onboardingStatus !== DriverOnboardingStatus.Onboarded
          ) {
            this.addOnboardingMenuEntries();
            this.router.navigate([this.onboardingEntry.url]);
            return;
          }

          if (userData.role !== UserRole.Manager) {
            this.qualificationsService
              .getQualifications()
              .then((qualifications) => {
                this.profileService.statusBonusPercent =
                  qualifications.bonusPercent ?? null;
              })
              .catch(() => {
                // Non-fatal — getQualifications already reports to Sentry, and
                // the bonus UI falls back to default percentages.
              });
          }

          await this.trainingsService.fetchTrainings(userData.role);

          this.logger.info({
            message: 'Syncing MoEngage user profile',
            identifier: 'app_component_moengage_sync_user_profile',
            method: 'ngOnInit.authSubject$.getUserData',
            value: { profile: JSON.stringify(profile) },
          });

          await this.moEngageService.syncUserProfile(
            profile,
            this.trainingsService.basicTrainings(),
          );

          const currentPremiumPartnerLevel =
            this.profileService.premiumPartnerLevel;

          const newPremiumPartnerLevel =
            await this.trainingsService.getPremiumPartnerLevelByBasicTrainingsCompletion(
              this.trainingsService.basicTrainings(),
            );

          if (newPremiumPartnerLevel !== currentPremiumPartnerLevel) {
            this.profileService.updatePremiumPartnerLevelInSalesforce(
              currentPremiumPartnerLevel,
              newPremiumPartnerLevel,
            );

            this.profileService.premiumPartnerLevel = newPremiumPartnerLevel;
          }

          if (
            this.profileService.premiumPartnerLevel === 0 ||
            newPremiumPartnerLevel === 0
          ) {
            this.trainingsService.showOnboardingModal();
          }

          const company = await lastValueFrom(
            this.companyService.fetchCompanyProfile(),
          );

          if (!company) {
            return;
          }

          this.companyService.company = company;

          await this.vehicleInspectionService.loadInspections();
        },
        error: this.toastService.showErrorToast,
      });
    });
  }

  ngAfterViewInit() {
    const applyChatWidgetStyles = () => {
      const chatWidget = document.querySelector('chat-widget');

      if (!chatWidget) {
        return;
      }

      const shadowRoot = chatWidget.shadowRoot;

      if (!shadowRoot) {
        return;
      }

      const mainContainer = shadowRoot.querySelector(
        '.h-full.w-full.flex.flex-col',
      );

      if (!mainContainer) {
        return;
      }

      (mainContainer as HTMLElement).style.setProperty(
        'max-height',
        '90%',
        'important',
      );
    };

    setTimeout(() => {
      applyChatWidgetStyles();
      this.chatBubbleDragService.init();
    }, 500);

    this.observer = new MutationObserver(() => {
      applyChatWidgetStyles();
      this.chatBubbleDragService.init();
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  changeLanguage() {
    this.languageWasChanged = true;
    this.languagesService.languageChange();
  }

  toggleAccordion = () => {
    const isMobileView = window.innerWidth < 992;

    if (!isMobileView) {
      return;
    }

    const nativeEl = this.accordionGroup;
    const nativeElValue = nativeEl.value === 'legal' ? 'undefined' : 'legal';

    nativeEl.value = nativeElValue;
  };

  async logout() {
    this.removeAdminTab();
    this.adminInspectionService.clearToken();
    this.adminMode = false;
    this.adminMenuActive = false;
    this.currentUserRole = undefined;

    this.logger.info({
      message: 'Resetting MoEngage user on logout',
      identifier: 'app_component_moengage_reset_user',
      method: 'logout',
    });

    await this.moEngageService.resetUser();

    return this.authService.logout().subscribe({
      next: async () => {
        await this.router.navigate(['login']);
      },
      error: this.toastService.showErrorToast,
    });
  }

  forceAnError() {
    if (this.shouldForceErrorCount < 5) {
      this.shouldForceErrorCount++;

      return;
    }

    this.shouldForceErrorCount = 0;

    throw new Error('MovacarPRO: This error is just a test!');
  }

  forceASentryError() {
    try {
      this.forceAnError();
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  private setLanguageToUserLanguage(userData: UserData) {
    this.languagesService.activeLanguage =
      this.languagesService.languages.find(
        (lan) => lan.key === userData.language.toLowerCase(),
      ) || this.languagesService.activeLanguage;

    this.languagesService.languageChange();
  }

  private async startFirebase() {
    await this.firebaseAnalyticsService.initializeFirebase();
    await this.firebaseAnalyticsService.attachToRouter();
  }

  isAdminUser(userData: UserData): boolean {
    const userEmail = userData.email;

    const isAdmin =
      userEmail.endsWith('movacar.com') || userEmail.endsWith('movacar.de');

    this.adminMode = isAdmin;

    return isAdmin;
  }

  private async loadAdminState() {
    const savedAdminMode = await this.storageService.get('adminMode');
    const savedAdminMenu = await this.storageService.get('adminMenuActive');

    if (savedAdminMode) {
      this._adminMode = savedAdminMode === 'true';
    }

    if (savedAdminMenu) {
      this._adminMenuActive = savedAdminMenu === 'true';
    }
  }

  addAdminModeTab() {
    if (!this.adminMode) {
      this.userPages = this.getOriginalMenuItems();

      return;
    }

    this.pages = this.pages.filter((page) => page.title !== 'Admin mode');

    this.pages = [
      {
        title: 'Admin mode',
        url: '/admin',
        icon: 'construct',
        roles: this.allRoles,
      },
      ...this.pages,
    ];
  }

  removeAdminTab() {
    return (this.pages = this.pages.filter(
      (page) => page.title !== 'Admin mode',
    ));
  }

  openAdminMenu() {
    this.adminMenuActive = true;
  }

  closeAdminMode() {
    this.adminMenuActive = false;

    this.router.navigate(['transfers']);
  }

  getOriginalMenuItems() {
    return (this.pages = [...this.standardPages]);
  }

  private addOnboardingMenuEntries(): void {
    const qualifications = this.standardPages.find(
      (p) => p.url === '/qualifications/',
    );

    if (
      qualifications &&
      !this.userPages.some((p) => p.url === '/qualifications/')
    ) {
      this.userPages = [...this.userPages, qualifications];
    }

    // The business profile appears only for roles whose onboarding path includes
    // the company step (a Driver skips it), and only once its working phase is
    // reached — for the full path that's after the profile step, for a Manager
    // (company is their first step) immediately. Mirrors the onboarding stepper.
    const businessProfile = this.profilePages.find(
      (p) => p.url === '/company/',
    );

    const steps = this.profileService.onboardingSteps;

    const companyUnlocked =
      steps.includes(DriverOnboardingStatus.CompanyCompleted) &&
      onboardingStageProgress(
        steps,
        this.profileService.onboardingStatus,
        DriverOnboardingStatus.CompanyCompleted,
      ).reached;

    if (
      businessProfile &&
      companyUnlocked &&
      !this.userProfilePages.some((p) => p.url === '/company/')
    ) {
      this.userProfilePages = [...this.userProfilePages, businessProfile];
    }
  }

  async initializeApp() {
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        const url = event.url;

        if (url.includes('/identity-check/')) {
          const parsed = new URL(url);
          const slug = parsed.pathname + parsed.search;

          this.router.navigateByUrl(slug);

          return;
        }

        if (!url.includes('reset-password') && !url.includes('rph=')) {
          const slug = event.url.split('.com').pop();

          if (slug) {
            this.router.navigateByUrl(slug);
          }

          return;
        }

        if (!this.isNativePlatform) {
          await Browser.open({ url });

          return;
        }

        const urlParams = new URLSearchParams(url.split('?')[1] || '');
        const rph = urlParams.get('rph');

        if (!rph) {
          this.toastService.showToast(
            'danger',
            'movacarpro_error_invalid_reset_password_url',
          );

          this.router.navigate(['/login']);

          return;
        }

        this.router.navigate(['/login/reset-password'], {
          queryParams: {
            rph: rph,
          },
        });
      });
    });
  }

  getLogo() {
    return this.currentUserRole &&
      this.currentUserRole === UserRole.FleetManager
      ? 'assets/logo-fleet.svg'
      : 'assets/logo.svg';
  }

  getLogoClass() {
    return this.currentUserRole &&
      this.currentUserRole === UserRole.FleetManager
      ? 'fleet-logo'
      : 'logo';
  }

  isBonusPanelOpen = false;

  private static readonly BONUS_PANEL_DISMISSED_KEY = 'bonusPanelDismissed';

  onInfoIconClick() {
    this.isBonusPanelOpen = !this.isBonusPanelOpen;

    if (!this.isBonusPanelOpen && this.isNativePlatform) {
      this.preferencesService.set(
        AppComponent.BONUS_PANEL_DISMISSED_KEY,
        'true',
      );
    }
  }

  private async initBonusPanelState(): Promise<void> {
    if (!this.isNativePlatform) {
      return;
    }

    const dismissed = await this.preferencesService.get(
      AppComponent.BONUS_PANEL_DISMISSED_KEY,
    );

    this.isBonusPanelOpen = dismissed !== 'true';
  }

  get profileHasMissingFields(): boolean {
    return !isProfileComplete(this.profileService.profile$.value);
  }

  get companyHasMissingFields(): boolean {
    return !isCompanyComplete(
      this.companyService.company$.value,
      this.currentUserRole === UserRole.SingleDriver,
    );
  }

  shouldDisplayStatusBadge() {
    return (
      this.authService.role !== UserRole.FleetManager &&
      this.authService.role !== UserRole.Manager
    );
  }

  shouldDisplayBonusInfo() {
    return (
      this.shouldDisplayStatusBadge() &&
      getBonusPercent(
        this.profileService.premiumPartnerLevel,
        this.profileService.statusBonusPercent,
      ) > 0
    );
  }

  get isDriverOnboarded(): boolean {
    return (
      this.profileService.onboardingStatus === DriverOnboardingStatus.Onboarded
    );
  }

  get showOnboardingStateBadge(): boolean {
    return (
      this.shouldDisplayStatusBadge() &&
      this.profileService.onboardingEnabled &&
      !!this.profileService.onboardingStatus &&
      !this.isDriverOnboarded
    );
  }

  get showTrainingRequiredBadge(): boolean {
    return (
      this.shouldDisplayStatusBadge() &&
      this.isDriverOnboarded &&
      !this.profileService.premiumPartnerLevel
    );
  }

  ngOnDestroy(): void {
    if (!this.observer) {
      return;
    }

    this.observer.disconnect();
  }
}
